import * as React from 'react';
import { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Divider,
    CircularProgress,
    Alert,
    Grid,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { meetingManager } from '../api/meetings';

interface CompleteMeeting {
    taskId: string | null;
    onSuccess?: () => void;
    onClose?: () => void;
    refetch?: () => void;
};

const CompleteMeeting: React.FC<CompleteMeeting> = ({ taskId, onSuccess, onClose, refetch }) => {

    const [notes, setNotes] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const handleComplete = React.useCallback(async () => {
        if (!taskId || !notes.trim()) {
            setError('Please enter completion notes');
            return;
        }
        try {
            setLoading(true);
            setError(null);

            const updates = [
                {
                    "update_key": "notes",
                    "new_value": notes.trim()
                },
                {
                    "update_key": "is_completed",
                    "new_value": "true"
                }
            ];
            await meetingManager.update(taskId, updates);
            refetch?.();
            onSuccess?.();
            onClose?.();
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to mark meeting as complete';
            setError(errorMessage);
            console.error('Failed to complete meeting:', err);
        } finally {
            setLoading(false);
        }
    }, [taskId, notes, onSuccess, onClose, refetch]);

    const isFormValid = notes.trim().length > 0;

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3, height: '100%' }}>
            
            {error && (
                <Box sx={{ px: 2, pt: 2 }}>
                    <Alert severity="error" onClose={() => setError(null)}>
                        {error}
                    </Alert>
                </Box>
            )}

            <Box sx={{ px: 2, py: 2, flex: 1, overflow: 'auto' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Completion Notes <span style={{ color: 'red' }}>*</span>
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={5}
                        placeholder="Describe meeting outcomes and any relevant details..."
                        value={notes}
                        onChange={(e) => {
                            setNotes(e.target.value);
                            if (error) setError(null);
                        }}
                        disabled={loading}
                        error={!isFormValid && notes !== ''}
                        helperText={!isFormValid && notes !== '' ? 'Please enter completion notes' : ''}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                backgroundColor: theme => theme.palette.mode === 'light' ? '#fafafa' : '#303030',
                            },
                        }}
                    />
                </Box>
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    px: 3,
                    py: 2,
                    backgroundColor: theme => theme.palette.mode === 'light' ? '#f5f5f5' : '#424242',
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onClose}
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleComplete}
                    disabled={loading || !isFormValid}
                    sx={{
                        minWidth: 150,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    {loading && <CircularProgress size={20} />}
                    {loading ? 'Marking Complete...' : 'Mark Complete'}
                </Button>
            </Box>
        </Box>
    );

};

export default CompleteMeeting;